import { dedupeMediaInputsByKeys } from 'utils/mediaInputDedupe'
import { mediaInputIdentityValues, type MediaInputIdentity } from 'utils/mediaInputIdentity'

type NbComposerMediaInput = MediaInputIdentity & {
    file?: File | null
    url?: string | null
    label?: string | null
    contentType?: string | null
    content_type?: string | null
}

type NbComposerMediaInputAttrs = Record<string, any> & {
    imageInput?: NbComposerMediaInput | null
    imageInputUrl?: string | null
    audioInput?: NbComposerMediaInput | null
    audioInputUrl?: string | null
    referenceImageInputs?: NbComposerMediaInput[]
    referenceVideoInputs?: NbComposerMediaInput[]
    referenceAudioInputs?: NbComposerMediaInput[]
}

type NbComposerMediaCandidates<
    TImage extends NbComposerMediaInput,
    TVideo extends NbComposerMediaInput,
    TAudio extends NbComposerMediaInput,
> = {
    images: TImage[]
    videos: TVideo[]
    audios: TAudio[]
}

const mediaFileKey = (file?: File | null) => {
    if (!file) return ''
    return [file.name, file.size, file.lastModified, file.type].join(':')
}

const normalizedMediaUrl = (url?: string | null) => {
    if (!url) return ''
    if (url.startsWith('data:') || url.startsWith('blob:')) return url
    return url.split('?')[0].split('#')[0]
}

const dedupeMediaInputs = <T extends NbComposerMediaInput>(inputs: T[]) =>
    dedupeMediaInputsByKeys(inputs, input => ({
        identityValues: mediaInputIdentityValues(input),
        fileKey: mediaFileKey(input.file),
        urlKey: normalizedMediaUrl(input.url),
    }))

const isBlobMediaUrl = (url?: string | null) => !!url && url.startsWith('blob:')

const mediaInputContentType = (input?: NbComposerMediaInput | null) =>
    input?.contentType || input?.content_type || ''

const composerInputReplacesExisting = (
    composerInput: NbComposerMediaInput,
    existingInput: NbComposerMediaInput,
) => {
    const composerIdentities = new Set(mediaInputIdentityValues(composerInput))
    if (
        mediaInputIdentityValues(existingInput).some(identity => composerIdentities.has(identity))
    ) {
        return true
    }

    if (
        normalizedMediaUrl(composerInput.url) &&
        normalizedMediaUrl(composerInput.url) === normalizedMediaUrl(existingInput.url)
    ) {
        return true
    }

    return !!(
        !isBlobMediaUrl(composerInput.url) &&
        isBlobMediaUrl(existingInput.url) &&
        composerInput.label &&
        composerInput.label === existingInput.label &&
        mediaInputContentType(composerInput) === mediaInputContentType(existingInput)
    )
}

const mergeComposerMediaInputs = (
    composerInputs: NbComposerMediaInput[],
    existingInputs: NbComposerMediaInput[],
) => {
    const retainedExistingInputs = existingInputs.filter(
        existingInput =>
            !composerInputs.some(composerInput =>
                composerInputReplacesExisting(composerInput, existingInput),
            ),
    )
    return dedupeMediaInputs([...composerInputs, ...retainedExistingInputs])
}

const inputFromAttrs = (
    attrs: NbComposerMediaInputAttrs,
    kind: 'image' | 'audio',
): NbComposerMediaInput | null => {
    const input = attrs[`${kind}Input`]
    if (input) return input
    const url = attrs[`${kind}InputUrl`]
    return url ? { url } : null
}

export const partitionNbComposerMediaInputs = <
    TImage extends NbComposerMediaInput,
    TVideo extends NbComposerMediaInput,
    TAudio extends NbComposerMediaInput,
>({
    images,
    videos,
    audios,
}: NbComposerMediaCandidates<TImage, TVideo, TAudio>) => {
    const uniqueImages = dedupeMediaInputs(images)
    const uniqueVideos = dedupeMediaInputs(videos)
    const uniqueAudios = dedupeMediaInputs(audios)

    return {
        imageInput: uniqueImages[0] || null,
        audioInput: uniqueAudios[0] || null,
        referenceImageInputs: uniqueImages.slice(1),
        referenceVideoInputs: uniqueVideos,
        referenceAudioInputs: uniqueAudios.slice(1),
    }
}

export const mergeNbComposerMediaInputAttrs = (
    existingAttrs: NbComposerMediaInputAttrs,
    composerAttrs: NbComposerMediaInputAttrs,
): NbComposerMediaInputAttrs => {
    const existingImage = inputFromAttrs(existingAttrs, 'image')
    const composerImage = inputFromAttrs(composerAttrs, 'image')
    const imageInput = composerImage || existingImage
    const displacedImage =
        existingImage &&
        composerImage &&
        !composerInputReplacesExisting(composerImage, existingImage)
            ? [existingImage]
            : []
    const referenceImageInputs = mergeComposerMediaInputs(
        [...(composerAttrs.referenceImageInputs || []), ...displacedImage],
        existingAttrs.referenceImageInputs || [],
    ).filter(input => !imageInput || !composerInputReplacesExisting(imageInput, input))

    const existingAudio = inputFromAttrs(existingAttrs, 'audio')
    const composerAudio = inputFromAttrs(composerAttrs, 'audio')
    const audioInput = composerAudio || existingAudio
    const displacedAudio =
        existingAudio &&
        composerAudio &&
        !composerInputReplacesExisting(composerAudio, existingAudio)
            ? [existingAudio]
            : []
    const referenceAudioInputs = mergeComposerMediaInputs(
        [...(composerAttrs.referenceAudioInputs || []), ...displacedAudio],
        existingAttrs.referenceAudioInputs || [],
    ).filter(input => !audioInput || !composerInputReplacesExisting(audioInput, input))

    const referenceVideoInputs = mergeComposerMediaInputs(
        composerAttrs.referenceVideoInputs || [],
        existingAttrs.referenceVideoInputs || [],
    )

    return {
        ...existingAttrs,
        hasImageInput: !!imageInput,
        imageInput,
        imageInputUrl: imageInput?.url || null,
        imageInputLabel: imageInput?.label || null,
        hasAudioInput: !!audioInput,
        audioInput,
        audioInputUrl: audioInput?.url || null,
        audioInputLabel: audioInput?.label || null,
        audioInputContentType: mediaInputContentType(audioInput),
        referenceImageInputs,
        referenceVideoInputs,
        referenceAudioInputs,
    }
}
